/**
 * webVitals — SSOT shim around the `web-vitals` package.
 *
 * Captures the Core Web Vitals (LCP, CLS, INP) plus the supporting
 * load-timing signals (TTFB, FCP) and forwards them to the existing
 * `window.__ANALYTICS__` sink. Threshold breaches are additionally
 * surfaced through `captureError` so the perf-budget regressions
 * show up in the same observability stream as caught exceptions.
 *
 * Why this is the SSOT:
 *   - One place to import & wire the `web-vitals` callbacks.
 *   - One place to define the "poor" thresholds we treat as a breach,
 *     so the Lighthouse-CI assertions in `lighthouserc.json` and the
 *     runtime alert path stay in sync.
 *   - `captureError` already knows how to fan out to Sentry + analytics
 *     without throwing on missing sinks; reusing it keeps the surface
 *     area tiny and avoids a parallel telemetry path.
 *
 * Wiring:
 *   - Mount once from `src/main.tsx` (after `createRoot`).
 *   - The `web-vitals` library is event-driven; calling `reportWebVitals`
 *     a second time is a no-op except for an extra handler set, which
 *     is harmless but wasteful — guard with the module-local flag.
 *
 * Thresholds (Core Web Vitals "poor" cutoffs as of 2026-Q1):
 *   - LCP > 2500 ms  → poor
 *   - CLS > 0.10     → poor
 *   - INP > 200 ms   → poor
 *   - TTFB > 800 ms  → poor (informational)
 *   - FCP > 1800 ms  → poor (informational)
 *
 * These match the assertion budget in `lighthouserc.json`.
 */

import type { Metric } from 'web-vitals';
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import { captureError } from '../errors/captureError';

/** Poor-rating thresholds — kept in sync with `lighthouserc.json`. */
export const WEB_VITAL_THRESHOLDS = {
    LCP: 2500,
    CLS: 0.10,
    INP: 200,
    TTFB: 800,
    FCP: 1800,
} as const;

type AnalyticsLike = {
    track?: (event: string, payload?: Record<string, unknown>) => void;
};

let mounted = false;

function readAnalytics(): AnalyticsLike | null {
    if (typeof window === 'undefined') return null;
    const w = window as unknown as Record<string, unknown>;
    const a = w.__ANALYTICS__;
    return (a && typeof a === 'object') ? a as AnalyticsLike : null;
}

/**
 * Forward a single Web-Vital metric to analytics + (when over budget)
 * to captureError. Exported for tests; production callers use
 * `reportWebVitals`.
 */
export function handleWebVital(metric: Metric): void {
    const threshold = WEB_VITAL_THRESHOLDS[metric.name];
    const overBudget = typeof threshold === 'number' && metric.value > threshold;

    // 1) Always emit to analytics so we can chart the distribution.
    const analytics = readAnalytics();
    if (analytics && typeof analytics.track === 'function') {
        // Why: telemetry sinks must NOT propagate failure — this is the
        // canonical legitimate exception to chiefy/no-silent-catch that
        // captureError.ts:78 spells out, mirrored here because the analytics
        // path is direct (not routed through captureError) when the metric
        // is within budget. If the sink throws (CSP block, SDK init race),
        // a perf reporter must NOT break the user-facing flow that already
        // handled its own error. Same authorial intent unit as captureError.
        /* eslint-disable chiefy/no-silent-catch -- telemetry failure must not propagate, see core/errors/captureError.ts:78 */
        try {
            analytics.track('web_vital', {
                name: metric.name,
                value: metric.value,
                rating: metric.rating,
                delta: metric.delta,
                id: metric.id,
                navigationType: metric.navigationType,
                overBudget,
            });
        } catch {
            // Why: telemetry sinks must NOT propagate failure (see core/errors/captureError.ts:78).
        }
        /* eslint-enable chiefy/no-silent-catch */
    }

    // 2) Threshold breach → captureError so it surfaces in the same
    //    observability stream as caught exceptions. We wrap it as a
    //    synthetic Error so Sentry's stack-frame UI stays useful.
    if (overBudget) {
        const err = new Error(
            `Web-vital ${metric.name} over budget: ${metric.value.toFixed(2)} > ${threshold}`,
        );
        captureError(err, {
            scope: 'webVitals',
            op: metric.name,
            extra: {
                value: metric.value,
                threshold,
                rating: metric.rating,
                navigationType: metric.navigationType,
            },
        });
    }
}

/**
 * Attach handlers for LCP, CLS, INP, TTFB, FCP. Idempotent — calling
 * twice is a no-op so an accidental double-mount can't double-report.
 */
export function reportWebVitals(): void {
    if (mounted) return;
    mounted = true;
    onLCP(handleWebVital);
    onCLS(handleWebVital);
    onINP(handleWebVital);
    onTTFB(handleWebVital);
    onFCP(handleWebVital);
}

/**
 * Test-only reset. Vitest reuses the module across tests, so the
 * `mounted` latch otherwise sticks. Not exported through any
 * barrel — webVitals.test imports it by name.
 */
export function __resetForTests(): void {
    mounted = false;
}
